import { createContext, useCallback, useContext, useEffect, useState } from 'react'

const ThemeContext = createContext(null)
const STORAGE_KEY = 'storyfy_theme'

export const useTheme = () => {
  const ctx = useContext(ThemeContext)
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return ctx
}

const prefersDark = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-color-scheme: dark)').matches

const resolve = (pref) => (pref === 'system' ? (prefersDark() ? 'dark' : 'light') : pref)

const applyClass = (resolved) => {
  document.documentElement.classList.toggle('dark', resolved === 'dark')
}

export const ThemeProvider = ({ children }) => {
  // Preference: 'light' | 'dark' | 'system'. Defaults to 'system' (no stored value).
  const [theme, setThemeState] = useState(
    () => localStorage.getItem(STORAGE_KEY) || 'system',
  )
  const [resolvedTheme, setResolvedTheme] = useState(() => resolve(theme))

  // Apply the resolved theme whenever the preference changes.
  useEffect(() => {
    const r = resolve(theme)
    setResolvedTheme(r)
    applyClass(r)
  }, [theme])

  // Follow the OS while the preference is 'system'.
  useEffect(() => {
    if (theme !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = () => {
      const r = prefersDark() ? 'dark' : 'light'
      setResolvedTheme(r)
      applyClass(r)
    }
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [theme])

  const setTheme = useCallback((next) => {
    setThemeState(next)
    if (next === 'system') {
      localStorage.removeItem(STORAGE_KEY)
    } else {
      localStorage.setItem(STORAGE_KEY, next)
    }
  }, [])

  return (
    <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}
